'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

interface UseAudioRecorderOptions {
  onFile: (file: File) => void
}

interface UseAudioRecorderResult {
  recording: boolean
  seconds: number
  start: () => Promise<void>
  stop: () => void
}

export function useAudioRecorder(
  opts: UseAudioRecorderOptions,
): UseAudioRecorderResult {
  const [recording, setRecording] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const onFileRef = useRef(opts.onFile)
  useEffect(() => {
    onFileRef.current = opts.onFile
  }, [opts.onFile])

  const cleanup = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    for (const track of streamRef.current?.getTracks() ?? []) track.stop()
    streamRef.current = null
    mediaRecorderRef.current = null
    chunksRef.current = []
  }, [])

  const start = useCallback(async () => {
    if (mediaRecorderRef.current) return

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    streamRef.current = stream
    const recorder = new MediaRecorder(stream)
    mediaRecorderRef.current = recorder
    chunksRef.current = []

    recorder.addEventListener('dataavailable', (e) => {
      chunksRef.current.push(e.data)
    })

    recorder.addEventListener('stop', () => {
      const ext = recorder.mimeType.split('/')[1]?.split(';')[0] ?? 'webm'
      const file = new File(chunksRef.current, `recording.${ext}`, {
        type: recorder.mimeType,
      })
      if (file.size > 0) onFileRef.current(file)
      cleanup()
    })

    recorder.start()
    setRecording(true)
    setSeconds(0)
    intervalRef.current = setInterval(() => {
      setSeconds((s) => s + 1)
    }, 1000)
  }, [cleanup])

  const stop = useCallback(() => {
    setRecording(false)
    setSeconds(0)
    if (!mediaRecorderRef.current) return
    mediaRecorderRef.current.stop()
  }, [])

  useEffect(() => cleanup, [cleanup])

  return { recording, seconds, start, stop }
}
